import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, Menu, User, LogOut, ChevronRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useOnboarding } from '../contexts/OnboardingContext';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

// Apple-inspired color palette
const colors = {
  // Primary accent - deeper blue for a more formal, enterprise feel
  accent: '#1e40af',
  accentHover: '#1e3a8a',
  accentLight: 'rgba(30, 64, 175, 0.1)',
  
  // Neutral tones
  white: '#ffffff',
  gray50: '#f9f9f9',
  gray100: '#f3f3f3',
  gray200: '#e6e6e6',
  gray300: '#d1d1d1',
  gray500: '#8e8e8e',
  gray700: '#636363',
  gray900: '#333333',
};

// Global font styles
const fontStyles = {
  fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif",
  heading: {
    fontWeight: 600, // Semi-bold for headings
    letterSpacing: '-0.03em',
  },
  body: {
    fontWeight: 400, // Regular for body text
    letterSpacing: '-0.01em',
  },
  medium: {
    fontWeight: 500, // Medium weight
  },
  light: {
    fontWeight: 300, // Light weight
  }
};

// Animation variants
const navItemVariants = {
  hidden: { opacity: 0, y: -10 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.1,
      duration: 0.5,
      ease: [0.6, -0.05, 0.01, 0.99]
    }
  })
};

const navLogoVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: { 
    opacity: 1, 
    x: 0,
    transition: { 
      duration: 0.6, 
      ease: [0.6, -0.05, 0.01, 0.99]
    }
  },
  hover: { 
    scale: 1.05,
    transition: { 
      type: 'spring', 
      stiffness: 400, 
      damping: 10 
    }
  }
};

const userDropdownVariants = {
  hidden: { opacity: 0, x: 20 },
  visible: { 
    opacity: 1, 
    x: 0, 
    transition: { 
      duration: 0.5, 
      ease: [0.6, -0.05, 0.01, 0.99],
      delay: 0.3
    }
  }
};

// Enhanced animation variants for Apple-style scroll effects
const navContainerVariants = {
  initial: {
    maxWidth: '100%',
    margin: '0',
    borderRadius: '6px',
    backgroundColor: 'rgba(255, 255, 255, 0)',
    backdropFilter: 'blur(0px)',
    height: '100px',
    boxShadow: '0 0 0 rgba(0, 0, 0, 0)',
    border: '1px solid rgba(255, 255, 255, 0)',
    paddingTop: '24px',
    paddingBottom: '24px'
  },
  lightScroll: {
    maxWidth: '100%',
    margin: '0',
    borderRadius: '10px',
    backgroundColor: 'rgba(255, 255, 255, 0.75)',
    backdropFilter: 'blur(16px)',
    height: '95px',
    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.04), 0 1px 3px rgba(0, 0, 0, 0.02)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    paddingTop: '22px',
    paddingBottom: '22px',
    transition: {
      duration: 0.6,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  },
  mediumScroll: {
    maxWidth: '85%',
    margin: '0 auto',
    borderRadius: '16px',
    backgroundColor: 'rgba(255, 255, 255, 0.92)',
    backdropFilter: 'blur(24px)',
    height: '88px',
    boxShadow: '0 4px 18px rgba(0, 0, 0, 0.08), 0 2px 6px rgba(0, 0, 0, 0.03)',
    border: '1px solid rgba(255, 255, 255, 0.4)',
    paddingTop: '20px',
    paddingBottom: '20px',
    transition: {
      duration: 0.7,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  },
  heavyScroll: {
    maxWidth: '75%',
    margin: '0 auto',
    borderRadius: '20px',
    backgroundColor: 'rgba(250, 250, 250, 1)',
    backdropFilter: 'blur(32px)',
    height: '82px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), 0 4px 16px rgba(0, 0, 0, 0.06), 0 2px 8px rgba(0, 0, 0, 0.04)',
    border: '1px solid rgba(255, 255, 255, 0.6)',
    paddingTop: '18px',
    paddingBottom: '18px',
    transition: {
      duration: 0.8,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};

// Logo scaling variants
const logoVariants = {
  initial: { scale: 1, height: '44px' },
  lightScroll: { 
    scale: 1, 
    height: '44px',
    transition: { duration: 0.3, ease: [0.25, 0.1, 0.25, 1.0] }
  },
  mediumScroll: { 
    scale: 1, 
    height: '44px',
    transition: { duration: 0.4, ease: [0.25, 0.1, 0.25, 1.0] }
  },
  heavyScroll: { 
    scale: 1, 
    height: '44px',
    transition: { duration: 0.5, ease: [0.25, 0.1, 0.25, 1.0] }
  }
};

const NavBar = ({ activePage = "" }) => {
  // Removed unused scrollY to avoid warnings
  const [scrollPhase, setScrollPhase] = useState('initial');
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { user, logout, needsOnboarding } = useAuth();
  const { startOnboarding } = useOnboarding();
  const navigate = useNavigate();
  const userDropdownRef = useRef(null);
  // Removed unused navControls to avoid lints

  // Navigation items with paths
  const navItems = [
    { name: 'Product', path: '/#workflow-section' },
    { name: 'Pricing', path: '/pricing' }
  ];

  // Enhanced scroll detection with throttling for performance
  useEffect(() => {
    let ticking = false;
    
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const currentScrollY = window.scrollY;
          
          // Determine scroll phase based on position
          if (currentScrollY === 0) {
            setScrollPhase('initial');
          } else if (currentScrollY > 0 && currentScrollY <= 100) {
            setScrollPhase('lightScroll');
          } else if (currentScrollY > 100 && currentScrollY <= 200) {
            setScrollPhase('mediumScroll');
          } else if (currentScrollY > 200) {
            setScrollPhase('heavyScroll');
          }
          
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target)) {
        setShowUserDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      setShowUserDropdown(false);
    } catch (error) {
      console.error('Failed to logout:', error);
    }
  };

  const handleDashboardClick = () => {
    navigate('/dashboard');
  };

  return (
    <>
      {/* Enhanced Navigation Container */}
      <motion.div
        className="fixed left-0 right-0 z-50"
        style={{
          willChange: 'transform, opacity',
          top: '12px',
          paddingLeft: scrollPhase === 'initial' ? '32px' : '16px',
          paddingRight: scrollPhase === 'initial' ? '32px' : '16px',
          paddingTop: scrollPhase === 'heavyScroll' ? '8px' : '16px',
          paddingBottom: scrollPhase === 'heavyScroll' ? '8px' : '16px',
        }}
        initial="initial"
        animate={scrollPhase}
        variants={navContainerVariants}
      >
        <motion.nav 
          className="flex justify-between items-center relative"
          style={{
            fontFamily: fontStyles.fontFamily,
          }}
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
        >
          {/* Logo with scaling animation */}
          <motion.div 
            className="flex items-center"
            variants={navLogoVariants}
            initial="hidden"
            animate="visible"
            whileHover="hover"
          >
            <motion.button
              onClick={() => {
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              className="focus:outline-none"
              whileTap={{ scale: 0.95 }}
            >
              <motion.img 
                src="/10.png" 
                alt="SignPact Logo" 
                className="transition-all duration-300 cursor-pointer"
                style={{ height: '44px' }}
                initial="initial"
                animate={scrollPhase}
                variants={logoVariants}
              />
            </motion.button>
          </motion.div>

          {/* Desktop Navigation - Centered */}
          <div className="hidden md:flex items-center justify-center absolute left-1/2 transform -translate-x-1/2">
                         <motion.div 
               className="flex items-center space-x-8"
               style={{
                 gap: scrollPhase === 'heavyScroll' ? '28px' : '32px',
                 transition: 'gap 0.3s ease'
               }}
             >
              {navItems.map((item, index) => (
                <motion.div
                  key={item.name}
                  custom={index}
                  variants={navItemVariants}
                  initial="hidden"
                  animate="visible"
                >
                  {item.path.startsWith('/#') ? (
                    <a
                      href={item.path}
                      className="text-sm font-medium transition-all duration-200 relative"
                      style={{
                        color: (activePage === item.name.toLowerCase() || 
                               (activePage === "" && item.name === "Home")) 
                          ? colors.accent 
                          : colors.gray700,
                        ...fontStyles.medium,
                        fontSize: '16px',
                      }}
                      onMouseOver={(e) => {
                        if (activePage !== item.name.toLowerCase() && 
                            !(activePage === "" && item.name === "Home")) {
                          e.currentTarget.style.color = colors.accent;
                          e.currentTarget.style.transform = 'translateY(-1px)';
                        }
                      }}
                      onMouseOut={(e) => {
                        if (activePage !== item.name.toLowerCase() && 
                            !(activePage === "" && item.name === "Home")) {
                          e.currentTarget.style.color = colors.gray700;
                          e.currentTarget.style.transform = 'translateY(0)';
                        }
                      }}
                    >
                      {item.name}
                    </a>
                  ) : item.isExternal ? (
                    <a 
                      href={item.path}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-medium transition-all duration-200 relative"
                      style={{
                        color: (activePage === item.name.toLowerCase() || 
                               (activePage === "" && item.name === "Home")) 
                          ? colors.accent 
                          : colors.gray700,
                        ...fontStyles.medium,
                        fontSize: '16px',
                      }}
                      onMouseOver={(e) => {
                        if (activePage !== item.name.toLowerCase() && 
                            !(activePage === "" && item.name === "Home")) {
                          e.currentTarget.style.color = colors.accent;
                          e.currentTarget.style.transform = 'translateY(-1px)';
                        }
                      }}
                      onMouseOut={(e) => {
                        if (activePage !== item.name.toLowerCase() && 
                            !(activePage === "" && item.name === "Home")) {
                          e.currentTarget.style.color = colors.gray700;
                          e.currentTarget.style.transform = 'translateY(0)';
                        }
                      }}
                    >
                      {item.name}
                    </a>
                  ) : (
                    <Link 
                      to={item.path}
                      className="text-sm font-medium transition-all duration-200 relative"
                      style={{
                        color: (activePage === item.name.toLowerCase() || 
                               (activePage === "" && item.name === "Home")) 
                          ? colors.accent 
                          : colors.gray700,
                        ...fontStyles.medium,
                        fontSize: '16px',
                      }}
                      onMouseOver={(e) => {
                        if (activePage !== item.name.toLowerCase() && 
                            !(activePage === "" && item.name === "Home")) {
                          e.currentTarget.style.color = colors.accent;
                          e.currentTarget.style.transform = 'translateY(-1px)';
                        }
                      }}
                      onMouseOut={(e) => {
                        if (activePage !== item.name.toLowerCase() && 
                            !(activePage === "" && item.name === "Home")) {
                          e.currentTarget.style.color = colors.gray700;
                          e.currentTarget.style.transform = 'translateY(0)';
                        }
                      }}
                    >
                      {item.name}
                    </Link>
                  )}
                </motion.div>
              ))}
            </motion.div>
          </div>

          {/* Mobile Menu Button */}
          <motion.button
            className="md:hidden text-gray-700 focus:outline-none z-50"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            whileTap={{ scale: 0.9 }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <Menu size={scrollPhase === 'heavyScroll' ? 22 : 24} />
          </motion.button>

          {/* User Profile or Auth Buttons with responsive sizing */}
          <AnimatePresence mode="wait">
            {user ? (
              <motion.div 
                className="relative hidden md:block"
                ref={userDropdownRef}
                variants={userDropdownVariants}
                initial="hidden"
                animate="visible"
              >
                <motion.button 
                  onClick={() => setShowUserDropdown(!showUserDropdown)}
                  className="flex items-center space-x-2 focus:outline-none rounded-full pl-2 pr-3 py-1.5 border border-transparent transition-all duration-200"
                  style={{ 
                    backgroundColor: showUserDropdown ? colors.gray100 : 'transparent',
                    transform: scrollPhase === 'heavyScroll' ? 'scale(0.9)' : 'scale(1)'
                  }}
                  whileHover={{ 
                    backgroundColor: colors.gray100,
                    borderColor: colors.gray200,
                    scale: scrollPhase === 'heavyScroll' ? 0.95 : 1.05
                  }}
                  whileTap={{ scale: scrollPhase === 'heavyScroll' ? 0.85 : 0.97 }}
                >
                  <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-400 to-purple-500 flex items-center justify-center">
                    <span className="text-white text-sm font-medium">
                      {user.name?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <ChevronDown 
                    size={scrollPhase === 'heavyScroll' ? 15 : 16} 
                    style={{color: colors.gray700}} 
                  />
                </motion.button>

                <AnimatePresence>
                  {showUserDropdown && (
                    <motion.div 
                      className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl border py-2 z-50" 
                      style={{borderColor: colors.gray200}}
                      initial={{ opacity: 0, y: 10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 10, scale: 0.95 }}
                      transition={{ duration: 0.2 }}
                    >
                      <div className="px-4 py-2 border-b" style={{borderColor: colors.gray200}}>
                        <p className="text-sm font-medium" style={{letterSpacing: '-0.01em'}}>{user.name}</p>
                        <p className="text-xs truncate" style={{color: colors.gray500, letterSpacing: '-0.01em'}}>{user.email}</p>
                        {needsOnboarding && (
                          <div className="mt-2 flex items-center space-x-2">
                            <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse"></div>
                            <span className="text-xs text-orange-600">Setup incomplete</span>
                          </div>
                        )}
                      </div>
                      <motion.button
                        onClick={handleDashboardClick}
                        className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center"
                        style={{color: colors.gray700, letterSpacing: '-0.01em'}}
                        whileHover={{ backgroundColor: colors.gray100 }}
                      >
                        <User size={16} className="mr-2" style={{color: colors.accent}} />
                        Dashboard
                      </motion.button>
                      {/* <motion.button
                        onClick={() => {
                          setShowUserDropdown(false);
                          navigate('/generation');
                        }}
                        className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center"
                        style={{color: colors.gray700, letterSpacing: '-0.01em'}}
                        whileHover={{ backgroundColor: colors.gray100 }}
                      >
                        <svg width="16" height="16" viewBox="0 0 16 16" className="mr-2" style={{color: colors.accent}}>
                          <path fill="currentColor" d="M8 3.5a.5.5 0 0 0-1 0V9a.5.5 0 0 0 .252.434l3.5 2a.5.5 0 0 0 .496-.868L8 8.71V3.5z"/>
                          <path fill="currentColor" d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm7-8A7 7 0 1 1 1 8a7 7 0 0 1 14 0z"/>
                        </svg>
                        AI Generation
                      </motion.button> */}
                      {needsOnboarding && (
                        <motion.button
                          onClick={() => {
                            setShowUserDropdown(false);
                            startOnboarding();
                          }}
                          className="w-full px-4 py-2 text-left text-sm hover:bg-orange-50 flex items-center"
                          style={{color: colors.gray700, letterSpacing: '-0.01em'}}
                          whileHover={{ backgroundColor: 'rgba(255, 165, 0, 0.1)' }}
                        >
                          <div className="w-4 h-4 mr-2 bg-orange-500 rounded-full flex items-center justify-center">
                            <span className="text-xs text-white">!</span>
                          </div>
                          Complete Setup
                        </motion.button>
                      )}
                      <motion.button
                        onClick={() => {
                          setShowUserDropdown(false);
                          navigate('/settings');
                        }}
                        className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center"
                        style={{color: colors.gray700, letterSpacing: '-0.01em'}}
                        whileHover={{ backgroundColor: colors.gray100 }}
                      >
                        <svg width="16" height="16" viewBox="0 0 16 16" className="mr-2" style={{color: colors.accent}}>
                          <path fill="currentColor" d="M8 12a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm0-1.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z"/>
                          <path fill="currentColor" d="M8 0L6.5 1.5 4.5 1 4 3l-2 .5L2.5 6 0 8l1.5 1.5L1 11.5 3 12l.5 2L6 14.5 8 16l1.5-1.5L11.5 15l.5-2 2-.5L14.5 10 16 8l-1.5-1.5L15 4.5 13 4l-.5-2L10 1.5 8 0z"/>
                        </svg>
                        Settings
                      </motion.button>
                      <motion.button
                        onClick={handleLogout}
                        className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center"
                        style={{color: colors.gray700, letterSpacing: '-0.01em'}}
                        whileHover={{ backgroundColor: colors.gray100 }}
                      >
                        <LogOut size={16} className="mr-2" style={{color: colors.accent}} />
                        Logout
                      </motion.button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ) : (
              <motion.div 
                className="hidden md:flex items-center"
                variants={userDropdownVariants}
                initial="hidden"
                animate="visible"
                                 style={{
                   gap: scrollPhase === 'heavyScroll' ? '10px' : '12px',
                   transition: 'gap 0.3s ease'
                 }}
              >
                <motion.button 
                  onClick={() => navigate('/login')} 
                  className="px-4 py-2 transition-all duration-200"
                                     style={{
                     backgroundColor: colors.gray100, 
                     color: colors.gray700,
                     fontSize: '16px',
                     letterSpacing: '-0.01em',
                     fontWeight: 500,
                     borderRadius: '16px',
                     paddingLeft: scrollPhase === 'heavyScroll' ? '18px' : '24px',
                     paddingRight: scrollPhase === 'heavyScroll' ? '18px' : '24px',
                     paddingTop: scrollPhase === 'heavyScroll' ? '10px' : '10px',
                     paddingBottom: scrollPhase === 'heavyScroll' ? '10px' : '10px',
                     boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.05)',
                   }}
                  whileHover={{ 
                    scale: 1.03, 
                    backgroundColor: colors.gray200 
                  }}
                  whileTap={{ scale: 0.97 }}
                >
                  Log In
                </motion.button>
                
                <motion.button 
                  onClick={() => navigate('/signup')} 
                  className="px-4 py-2 transition-all duration-200"
                  style={{
                    backgroundColor: colors.accent, 
                    color: colors.white,
                    fontSize: '16px',
                    letterSpacing: '-0.01em',
                    fontWeight: 500,
                    borderRadius: '16px',
                    paddingLeft: scrollPhase === 'heavyScroll' ? '18px' : '24px',
                    paddingRight: scrollPhase === 'heavyScroll' ? '18px' : '24px',
                    paddingTop: scrollPhase === 'heavyScroll' ? '10px' : '10px',
                    paddingBottom: scrollPhase === 'heavyScroll' ? '10px' : '10px',
                    boxShadow: 'inset 0 1px 2px rgba(255, 255, 255, 0.1), 0 4px 12px rgba(30, 64, 175, 0.15)',
                  }}
                  whileHover={{ 
                    scale: 1.03, 
                    boxShadow: "0 12px 20px -4px rgba(30, 64, 175, 0.2), 0 4px 8px -2px rgba(30, 64, 175, 0.1)" 
                  }}
                  whileTap={{ scale: 0.97 }}
                >
                  Get Started Free
                </motion.button>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.nav>
      </motion.div>

      {/* Mobile Menu - Enhanced */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div 
            className="fixed inset-0 bg-white z-40 flex flex-col pt-28 px-8 md:hidden"
            initial={{ opacity: 0, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '100%' }}
            transition={{ duration: 0.25, ease: [0.25, 0.1, 0.25, 1.0] }}
          >
            <div className="flex flex-col space-y-6">
              {navItems.map((item, index) => (
                <motion.div
                  key={item.name}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ 
                    opacity: 1, 
                    x: 0,
                    transition: { delay: index * 0.05 + 0.1 }
                  }}
                >
                  {item.path.startsWith('/#') ? (
                    <a
                      href={item.path}
                      className="text-xl font-medium py-2 border-b border-gray-100 flex justify-between items-center"
                      style={{
                        color: activePage === item.name.toLowerCase() ? colors.accent : colors.gray800
                      }}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {item.name}
                      <ChevronRight size={20} style={{color: colors.gray400}} />
                    </a>
                  ) : item.isExternal ? (
                    <a
                      href={item.path}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xl font-medium py-2 border-b border-gray-100 flex justify-between items-center"
                      style={{
                        color: activePage === item.name.toLowerCase() ? colors.accent : colors.gray800
                      }}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {item.name}
                      <ChevronRight size={20} style={{color: colors.gray400}} />
                    </a>
                  ) : (
                    <Link
                      to={item.path}
                      className="text-xl font-medium py-2 border-b border-gray-100 flex justify-between items-center"
                      style={{
                        color: activePage === item.name.toLowerCase() ? colors.accent : colors.gray800
                      }}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {item.name}
                      <ChevronRight size={20} style={{color: colors.gray400}} />
                    </Link>
                  )}
                </motion.div>
              ))}
            </div>

            {/* Mobile: Replace auth buttons with notice to use desktop */}
            <div className="mt-10">
              <div className="rounded-lg border px-4 py-3" style={{borderColor: colors.gray300, backgroundColor: colors.gray50}}>
                <p className="text-sm" style={{color: colors.gray700}}>
                  Dashboard isn't mobile-friendly as of now
                </p>
                <p className="text-sm mt-1" style={{color: colors.gray700}}>
                  would highly recommend using the web product to make an account and go forward.
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

    </>
  );
};

export default NavBar;